import { decode, JwtPayload } from 'jsonwebtoken';
import { throttle } from 'lodash';
import { AppContext, AppProps } from 'next/app';
import React, { useEffect, useState } from 'react';
import { NextPageContext } from 'next';
import { ApolloProvider, ApolloClient } from '@apollo/client';
import config from '../common/config';
import {
  checkNextGenSupport,
  detectDevice,
} from '../common/helpers/environment/DetectDeviceHelper';
import {
  DOCUMENT_CLICK,
  emitter,
  WINDOW_RESIZE,
  WINDOW_SCROLL,
} from '../common/helpers/ui/EventHelper';
import FeatureFlagsHelper from '../common/helpers/featureFlags/featureFlagsHelper';
import { withApollo } from '../lib/withApollo';
import { wrapper } from '../store';
import {
  updateCurrentPage,
  updateLoginStatus,
  updateUserAgentDetails,
} from '../store/common/slice';
import Error from './_error';
import { recordWebVitalsEvent } from '../common/analytics/events/webVitalEvents';
import { sendAnalytics } from '../common/analytics/analytics';
import { getAnalyticsSource } from '../common/analytics/helpers/sourceHelper';

import './../common/styles/style.scss';
import './../common/styles/normalize.scss';
import 'antd/dist/reset.css';

import { IncomingMessage, ServerResponse } from 'http';

/**
 * A type representing the Next.js request object with added properties.
 *
 * @remarks
 * Extends the standard Node.js `IncomingMessage` with `cookies` and `query`.
 */
type NextIncomingMessage = IncomingMessage & {
  cookies: { [key: string]: string };
  query: { [key: string]: string | string[] | undefined };
};

/**
 * A type representing the Next.js response object with added methods.
 *
 * @remarks
 * Extends the standard Node.js `ServerResponse` with a `redirect` method.
 */
type NextServerResponse = ServerResponse & {
  redirect: (url: string) => void;
};

export function reportWebVitals({
  id,
  name,
  label,
  value,
}: {
  id: string;
  name: string;
  label: string;
  value: number;
}) {
  if (label === 'web-vital') {
    const analyticsSource = getAnalyticsSource(window.location.pathname);
    sendAnalytics(recordWebVitalsEvent(id, name, value, analyticsSource));
  }
}

interface DecodedUser {
  display_name?: string;
  email?: string;
}

interface MyAppProps extends AppProps {
  apollo: ApolloClient<unknown>;
}

function MyApp({ Component, pageProps, apollo }: Readonly<MyAppProps>) {
  const [hasError, setHasError] = useState(false);

  // Error boundary equivalent
  const ErrorBoundary: React.FC<{ children: React.ReactNode }> = ({
    children,
  }) => {
    try {
      return <>{children}</>;
    } catch (error) {
      console.error('Error Boundary caught:', error);
      setHasError(true);
      return <Error />;
    }
  };

  useEffect(() => {
    if (config.isBrowser) {
      const handleClick = () => emitter.emit(DOCUMENT_CLICK);
      const handleResize = throttle(() => emitter.emit(WINDOW_RESIZE), 200);
      const handleScroll = throttle(() => emitter.emit(WINDOW_SCROLL), 200);

      document.addEventListener('click', handleClick);
      window.addEventListener('resize', handleResize, true);
      window.addEventListener('scroll', handleScroll, true);

      (
        window as unknown as { recaptchaOptions: { lang: string } }
      ).recaptchaOptions = { lang: 'th' };

      return () => {
        document.removeEventListener('click', handleClick);
        window.removeEventListener('resize', handleResize, true);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, []);

  if (hasError) {
    return <Error />;
  }

  return (
    <ApolloProvider client={apollo}>
      <ErrorBoundary>
        <Component {...pageProps} />
      </ErrorBoundary>
    </ApolloProvider>
  );
}

MyApp.getInitialProps = wrapper.getInitialPageProps(
  (store) => async (context) => {
    const { Component, ctx } = context as unknown as AppContext;
    const { req, res, pathname } = ctx as NextPageContext;

    const decodedQueryParams: Record<string, string> = {};
    Object.entries(ctx.query).forEach(([key, value]) => {
      decodedQueryParams[key] = decodeURI(value as string);
    });
    ctx.query = decodedQueryParams;

    if (req) {
      const typedReq = req as NextIncomingMessage;
      const typedRes = res as NextServerResponse;

      const { destination } = (typedReq.query ?? {}) as {
        destination?: string;
      };
      if (destination === 'login') {
        typedRes.redirect(config.loginUrl);
      }

      FeatureFlagsHelper.setServerSideCookies(typedReq.cookies);

      const loginCookie = typedReq.cookies['tol-user-session'];
      const deviceType = detectDevice(req.headers);
      const isNextGenSupported = checkNextGenSupport(req.headers);

      store.dispatch(
        updateUserAgentDetails({
          isNextGenSupported,
          deviceType,
        }),
      );

      if (loginCookie) {
        const decoded = decode(loginCookie) as DecodedUser | null;
        if (decoded?.display_name && decoded?.email) {
          const userDetails = decode(
            typedReq.cookies['tol-access-token'],
          ) as JwtPayload | null;
          const userId = userDetails?.username as string | undefined;
          const payload = {
            displayName: decoded.display_name,
            email: decoded.email,
            accessToken: typedReq.cookies['tol-access-token'],
            refreshToken: typedReq.cookies['tol-refresh-token'],
            userId,
          };
          store.dispatch(updateLoginStatus(payload));
        }
      }
    }

    const currentPage = pathname.substring(1);
    store.dispatch(updateCurrentPage(currentPage));

    return {
      pageProps: Component.getInitialProps
        ? await Component.getInitialProps(ctx)
        : {},
    };
  },
);

export default withApollo({ ssr: true })(wrapper.withRedux(MyApp));
